import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { IHighlight } from 'react-pdf-highlighter';
import type { ChatSource, StandoutItem, StandoutMarker } from '@libertify/ui';
import type { Cuepoint } from '../types';
import type { PdfViewerHandle } from '../components/PdfDocumentViewer';
import { buildSourceHighlight } from './buildSourceHighlight';
import { toChatSourcePosition } from './toChatSourcePosition';

export interface ExperienceDocument {
  id: string;
  url: string;
  name?: string;
  pageCount?: number | null;
}

export interface UseExperienceDocumentsArgs {
  /** Every document in the publication's `documents` map. */
  documents: ExperienceDocument[];
  /** The document that owns the pre-baked video-sync highlights (Document_0). */
  animatedDocumentId?: string;
  baseHighlights?: IHighlight[];
  baseCuepoints?: Cuepoint[];
  pdfViewerRef: RefObject<PdfViewerHandle | null>;
  sourceHighlightColor: string;
}

export interface UseExperienceDocumentsResult {
  documents: ExperienceDocument[];
  activeDocument?: ExperienceDocument;
  activeDocumentId: string;
  setActiveDocumentId: (id: string) => void;
  highlights: IHighlight[];
  cuepoints: Cuepoint[];
  /**
   * Video playback reached a cuepoint: show the animated document (its
   * highlights only exist there) and scroll to the matching highlight.
   */
  onCuepointStart: (cuepoint: Cuepoint) => void;
  /** Mark a source's zone and scroll to it. `marker` decides how it's drawn. */
  onSourceClick: (source: ChatSource, marker?: StandoutMarker) => void;
  /**
   * Mark the standout's first source zone with its own marker. No-op when
   * generation found no zones.
   */
  onStandoutSourceClick: (standout: StandoutItem) => void;
}

/** Append marks, replacing same-id ones so a re-click doesn't stack duplicates. */
const mergeMarks = <T>(
  prev: T[],
  next: T[],
  keyOf: (item: T) => string,
): T[] => {
  const incoming = new Set(next.map(keyOf));
  return [...prev.filter((item) => !incoming.has(keyOf(item))), ...next];
};

/**
 * Owns which document the viewer shows and derives its highlights / cuepoints.
 * Clicking a source (chatbot citation or standout) marks its zone and scrolls
 * there. Base video-sync highlights apply only on the animated document.
 *
 * Marks accumulate, and live here rather than in the tab content — so tab
 * switches keep them and only a reload clears them.
 */
export const useExperienceDocuments = ({
  documents,
  animatedDocumentId,
  baseHighlights = [],
  baseCuepoints = [],
  pdfViewerRef,
  sourceHighlightColor,
}: UseExperienceDocumentsArgs): UseExperienceDocumentsResult => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>();
  const defaultId = animatedDocumentId ?? documents[0]?.id ?? '';
  const activeDocumentId = selectedDocumentId ?? defaultId;
  const [sourceHighlights, setSourceHighlights] = useState<IHighlight[]>([]);
  const [sourceCuepoints, setSourceCuepoints] = useState<Cuepoint[]>([]);

  const activeDocument = useMemo(
    () => documents.find((d) => d.id === activeDocumentId),
    [documents, activeDocumentId],
  );

  const pendingScroll = useRef<IHighlight | null>(null);

  // Scroll only once the switch has rendered and the loader dropped the old
  // PDF — otherwise the outgoing document absorbs it.
  useEffect(() => {
    if (!pendingScroll.current) return;
    const timeout = setTimeout(() => {
      const highlight = pendingScroll.current;
      pendingScroll.current = null;
      if (!highlight) return;
      pdfViewerRef.current?.scrollViewerTo(highlight);
      // The timeout runs outside React, so nothing re-renders and the viewer
      // never repaints the mark — force a render (the copy is the cheapest
      // one). Keep this even if PdfViewer's highlights are ever memoized.
      setSourceHighlights((curr) => [...curr]);
    });
    return () => clearTimeout(timeout);
  }, [activeDocumentId, pdfViewerRef]);

  // Sources cite the project's own document ids, which aren't in the
  // publication's map (usually just the combined `Document_0`). The page falls
  // back to that combined document when the id doesn't resolve — which is the
  // animated one, so its highlights must stay (MED-6967).
  const isAnimatedActive = activeDocument
    ? activeDocumentId === animatedDocumentId
    : true;

  const highlights = useMemo<IHighlight[]>(() => {
    const base = isAnimatedActive ? baseHighlights : [];
    return [...base, ...sourceHighlights];
  }, [isAnimatedActive, baseHighlights, sourceHighlights]);

  const cuepoints = useMemo<Cuepoint[]>(() => {
    const base = isAnimatedActive ? baseCuepoints : [];
    return [...base, ...sourceCuepoints];
  }, [isAnimatedActive, baseCuepoints, sourceCuepoints]);

  const onCuepointStart = useCallback(
    (cuepoint: Cuepoint) => {
      const highlight = baseHighlights.find(
        (h) => h.id === cuepoint.highlightId,
      );
      if (!highlight) return;
      // Playback wins over a manual document pick: the cuepoint's highlight is
      // only drawn on the animated document, so go back to it before scrolling.
      if (animatedDocumentId && activeDocumentId !== animatedDocumentId) {
        setSelectedDocumentId(animatedDocumentId);
        pendingScroll.current = highlight;
        return;
      }
      pdfViewerRef.current?.scrollViewerTo(highlight);
    },
    [baseHighlights, animatedDocumentId, activeDocumentId, pdfViewerRef],
  );

  const onSourceClick = useCallback(
    (source: ChatSource, marker?: StandoutMarker) => {
      // Sources cite ids the publication's map may not hold. Resolve those to
      // the document the page falls back to (`defaultId`) rather than leaving
      // the viewer where it is — the coordinates belong to that combined
      // document, so a mark drawn on any other one lands at a random spot.
      const targetId = documents.some((d) => d.id === source.documentId)
        ? source.documentId
        : defaultId || activeDocumentId;
      const switchesDocument = targetId !== activeDocumentId;
      if (switchesDocument) {
        setSelectedDocumentId(targetId);
      }
      // No coordinates → the document switch above is all we can do.
      if (!source.position) return;

      const { highlight, cuepoint } = buildSourceHighlight({
        id: `source-${source.id}`,
        quote: source.quote,
        pageNumber: source.position.pageNumber,
        boundingRect: source.position.boundingRect,
        rects: source.position.rects,
        color: sourceHighlightColor,
        documentId: source.documentId,
        marker,
      });

      setSourceHighlights((prev) =>
        mergeMarks(prev, [highlight], (item) => item.id),
      );
      setSourceCuepoints((prev) =>
        mergeMarks(prev, [cuepoint], (item) => item.highlightId),
      );
      if (switchesDocument) {
        pendingScroll.current = highlight;
        return;
      }
      // Retry-aware scroll (see PdfViewer).
      pdfViewerRef.current?.scrollViewerTo(highlight);
    },
    [activeDocumentId, defaultId, documents, sourceHighlightColor, pdfViewerRef],
  );

  const onStandoutSourceClick = useCallback(
    (standout: StandoutItem) => {
      const [source] = standout.sources ?? [];
      if (!source) return;
      onSourceClick(
        {
          // Seeded from the standout so a re-click replaces its mark, not stacks it.
          id: `${standout.id}-0`,
          documentId: source.document_id,
          quote: source.zone.quote,
          pageNumber: source.zone.position.page_number,
          position: toChatSourcePosition(source.zone.position),
        },
        standout.marker,
      );
    },
    [onSourceClick],
  );

  return {
    documents,
    activeDocument,
    activeDocumentId,
    setActiveDocumentId: setSelectedDocumentId,
    highlights,
    cuepoints,
    onCuepointStart,
    onSourceClick,
    onStandoutSourceClick,
  };
};
